import React, { FC } from 'react';
import { Box, Paper, Divider, Typography } from '@mui/material';
import { MATH_FONT, Var, Op, Sub, Sum, Frac, Bracket } from './equationPrimitives';

// Interactive "AB-RWEA master equation" explainer. Each variable is hoverable
// and reveals what it maps to in the scorer (expertMatch.js). The maths:
//   S(e,p) = β(α) + ω(α) · [ J_w(U_e,V_p) · Σ_i ( ρ_i / Σ_j ρ_j ) · χ(t_i) ]
// i.e. affinity picks the band (β floor, ω width); attribute alignment ×
// experience-weighted profession relevance refines position within the band.
// The simple form above it — SCORE = band(α) + width(α)·(A·Q) — is the same
// thing in plain symbols (A = J_w, Q = the experience-weighted profession sum).

export const AlgorithmCard: FC = () => (
  <Paper sx={{ p: 3 }}>
    <Typography
      sx={{ fontStyle: 'italic', textAlign: 'center', color: 'var(--text)', maxWidth: 760, mx: 'auto' }}
    >
      A bounded, explainable matching system that leads with product-use affinity,
      then refines ranking with contextual alignment and experience-weighted
      expertise.
    </Typography>

    {/* SIMPLE form — the headline */}
    <Box
      sx={{
        fontFamily: MATH_FONT,
        fontSize: { xs: 20, sm: 30 },
        color: 'var(--text)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        rowGap: 1,
        pt: { xs: 3, sm: 4 },
        pb: 1,
      }}
    >
      <Var k="score" title="SCORE — the final 0–100% match for this expert × product. Affinity sets the floor; alignment × profession-fit fills the band above it. Bounded in [0, 1].">
        SCORE
      </Var>
      <Op>=</Op>

      <Var k="band" title="band(α) — the floor the score starts from, chosen by affinity. Exact category → 0.75, same family → 0.60, broad area → 0.40, none → 0.00. The band always wins: a perfect fit in the wrong category can't outrank the right one.">
        band
      </Var>
      <Op>(</Op>
      <Var k="alpha" title="α — category affinity. Does the expert actually work in this product's category? Exact leaf → 1.0, same family → 0.7, broad area → 0.4, none → 0.">α</Var>
      <Op>)</Op>
      <Op>+</Op>

      <Var k="width" title="width(α) — how much room there is to reorder experts inside the same affinity band, so fit refines but never overturns affinity.">
        width
      </Var>
      <Op>(</Op>
      <Var k="alpha" title="α — category affinity (same as above).">α</Var>
      <Op>)</Op>
      <Op>·</Op>
      <Op>(</Op>

      <Var k="A" title="A — attribute alignment ∈ [0,1]. Weighted-Jaccard overlap between the expert's attribute values and the product's (the weights you tune on the Attribute Weights tab). Equals J_w(U_e, V_p) in the full form below.">A</Var>
      <Op>·</Op>
      <Var k="Q" title="Q — profession fit ∈ [0,1]. How relevant the expert's professions are to this product, weighted by years of experience (the experience curve τ). Equals Σ(ρᵢ/Σρⱼ)·χ(tᵢ) in the full form below.">Q</Var>

      <Op>)</Op>
    </Box>

    <Typography
      sx={{ textAlign: 'center', color: '#5b7cba', fontWeight: 700, letterSpacing: 0.3 }}
    >
      The matching score, in one line
    </Typography>

    <Divider sx={{ my: 3, maxWidth: 420, mx: 'auto' }} />

    <Typography sx={{ textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>
      The same idea, written in full
    </Typography>

    {/* COMPLEX form — the full AB-RWEA master equation */}
    <Box
      sx={{
        fontFamily: MATH_FONT,
        fontSize: { xs: 17, sm: 23 },
        color: 'var(--text)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        rowGap: 1.5,
        py: { xs: 3, sm: 4 },
      }}
    >
      <Var k="score" title="S — the final match score for this expert × product. Bounded in [0, 1); higher is a better match.">
        S
      </Var>
      <Op>(</Op>
      <Var k="e" title="e — the expert being scored.">e</Var>
      <Op>,</Op>
      <Var k="p" title="p — the product being scored against.">p</Var>
      <Op>)</Op>
      <Op>=</Op>

      <Var k="band" title="β(α) — band floor. Affinity picks a disjoint band the score starts from: α=1 → 0.75, 0.7 → 0.60, 0.4 → 0.40, 0 → 0.00. The band strictly dominates ranking.">
        β
      </Var>
      <Op>(</Op>
      <Var k="alpha" title="α(e,p) — category affinity, the PRIMARY signal. Does the expert actually work in this product's category? Exact category → 1.0, same family → 0.7, broad area → 0.4, none → 0.">
        α
      </Var>
      <Op>)</Op>
      <Op>+</Op>

      <Var k="width" title="ω(α) — band width. How much room the refinement term has to reorder experts WITHIN the same affinity band (so affinity never gets overturned by attribute fit).">
        ω
      </Var>
      <Op>(</Op>
      <Var k="alpha" title="α(e,p) — category affinity (same as above).">α</Var>
      <Op>)</Op>

      <Bracket>[</Bracket>

      <Var k="A" title="J_w(U_e, V_p) — weighted-Jaccard attribute alignment (A ∈ [0,1]). Overlap between the product's and expert's attribute values, weighted by the attribute weights you tune below.">
        <span>J</span>
        <Sub>w</Sub>
      </Var>
      <Op>(</Op>
      <Var k="Ue" title="U_e — the expert's attribute-value vector (union across their professions). Each profession's vector is its sub-profession's attributes, refined by the expert's Area of Focus via per-group override: for each attribute group the Area of Focus sets, it replaces that group; groups it leaves blank inherit the sub-profession.">
        <span>U</span>
        <Sub>e</Sub>
      </Var>
      <Op>,</Op>
      <Var k="Vp" title="V_p — the product's attribute-value vector (from its product categories).">
        <span>V</span>
        <Sub>p</Sub>
      </Var>
      <Op>)</Op>
      <Op>·</Op>

      <Sum
        k="Q"
        title="Σ over the expert's professions — the experience-weighted profession-fit sum (this whole bracketed term is Q)."
        top={<Var k="N" title="N — the number of professions the expert holds.">N</Var>}
        bottom={
          <span>
            <Var title="i — index over the expert's professions.">i</Var>=1
          </span>
        }
      />

      <Frac
        num={
          <span>
            <Var k="rho" title="ρ_i(e,p) — profession relevance: how well the expert's i-th profession aligns with the product (same weighted-Jaccard kernel as A). Uses that profession's vector after the Area-of-Focus per-group override.">
              <span style={{ fontStyle: 'italic' }}>ρ</span>
              <Sub>i</Sub>
            </Var>
          </span>
        }
        den={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <Sum
              size={1.2}
              top={<span>N</span>}
              bottom={<span>j=1</span>}
            />
            <Var k="share" title="Σ ρ_j — normalizes each profession's relevance into a share, so the weights sum to 1 (a relevance-weighted average, immune to duplicate professions).">
              <span style={{ fontStyle: 'italic' }}>ρ</span>
              <Sub>j</Sub>
            </Var>
          </Box>
        }
      />

      <Var k="chi" title="χ(t_i) — experience factor for profession i: χ = 1 − e^(−tᵢ/τ). τ is the experience-curve constant you tune below; ~5 years → 0.63.">
        χ
      </Var>
      <Op>(</Op>
      <Var k="t" title="t_i — the expert's tenure (years) in profession i.">
        <span>t</span>
        <Sub>i</Sub>
      </Var>
      <Op>)</Op>

      <Bracket>]</Bracket>
    </Box>

    <Typography
      sx={{ textAlign: 'center', color: '#5b7cba', fontWeight: 700, letterSpacing: 0.3 }}
    >
      AB-RWEA master equation
    </Typography>
    <Typography sx={{ textAlign: 'center', color: 'var(--muted)', fontSize: 12, pt: 0.5 }}>
      Hover any symbol for its meaning.
    </Typography>
  </Paper>
);
